import { registerCrdtAlgorithm } from '@refinio/one.core/lib/crdts/CrdtAlgorithmRegistry.js';
import { calculateHashOfObj, calculateIdHashOfObj } from '@refinio/one.core/lib/util/object.js';
import { getAllIdObjectEntries, getAllEntries } from '@refinio/one.core/lib/reverse-map-query.js';
import { serializeWithType } from '@refinio/one.core/lib/util/promise.js';
import { createMessageBus } from '@refinio/one.core/lib/message-bus.js';
import { ensureHash, ensureIdHash } from '@refinio/one.core/lib/util/type-checks.js';
import { objectEvents } from '../misc/ObjectEventDispatcher.js';
import { OEvent } from '../misc/OEvent.js';
import { getCurrentVersion, getIdObject, getObjectByIdHash, getObjectByIdObj, getVersionsNodes, storeVersionedObject } from '@refinio/one.core/lib/storage-versioned-objects.js';
import { getObject, getObjectWithType, storeUnversionedObject } from '@refinio/one.core/lib/storage-unversioned-objects.js';
import { linkedListInsert } from './LinkedList/insert.js';
import { linkedListIterator } from './LinkedList/iterators.js';
import { LinkedListCrdtAlgorithm } from './LinkedList/LinkedListCrdtAlgorithm.js';
const MessageBus = createMessageBus('ChannelManager');
/**
 * Logs a channel manager message.
 *
 * @param participantsHash
 * @param owner
 * @param message
 */
function logWithParticipants(participantsHash, owner, message) {
    const shortHash = participantsHash ? participantsHash.substring(0, 8) : 'null';
    MessageBus.send('log', `${shortHash} # ${String(owner)} # ${message}`);
}
/**
 * Logs a channel manager message (debug level).
 *
 * @param participantsHash
 * @param owner
 * @param message
 */
function logWithParticipants_Debug(participantsHash, owner, message) {
    const shortHash = participantsHash ? participantsHash.substring(0, 8) : 'null';
    MessageBus.send('debug', `${shortHash} # ${String(owner)} # ${message}`);
}
/**
 *  This represents the possible orders of sorting the returned data from the channel.
 */
export var Order;
(function (Order) {
    Order[Order["Ascending"] = 1] = "Ascending";
    Order[Order["Descending"] = 2] = "Descending";
})(Order || (Order = {}));
/**
 * This model manages distributed lists of data in so called 'channels'.
 *
 * A channel is a list of objects stored as merkle-tree indexed by time.
 * The list is sorted by creation time so that it can be distributed and merged.
 *
 * Each channel is identified by a participants hash (SHA256Hash<HashGroup<Person>>) and the owner.
 * In a distributed network only the owner can create channels.
 * TODO: explain more about access rights and distribution and everything!
 *
 * The structure is as follows:
 * TODO: add PlantUml graph here
 *
 * NOTE: This class manages one global one object called ChannelRegistry
 *       It therefore does not make sense to have multiple ChannelManager objects.
 *       We don't use a singleton, because it makes it harder to track where
 *       channels are used.
 */
export default class ChannelManager {
    // Serialize locks
    static postLockName = 'ChannelManager_postLock';
    static postNELockName = 'ChannelManager_postNELock';
    static cacheLockName = 'ChannelManager_cacheLock_';
    static registryLockName = 'ChannelManager_registryLock';
    // Add instance ID for tracking
    instanceId = Math.random().toString(36).substring(2, 10);
    onUpdated = new OEvent();
    loadedRegistryVersion = undefined;
    channelInfoCache;
    disconnectOnVersionedObjListener = () => {
        // Empty by design
    };
    leuteModel;
    channelSettings = new Map();
    /**
     * The default owner when creating ot posting to a channel.
     *
     * This also changes the default author of posted data.
     *
     * If undefined, your main identity will be used.
     */
    defaultOwner;
    /**
     * Create the channel manager instance.
     */
    constructor(leuteModel) {
        this.leuteModel = leuteModel;
        this.channelInfoCache = new Map();
        MessageBus.send('debug', `[CHANNEL_INSTANCE] ChannelManager instance created with ID: ${this.instanceId}`);
    }
    /**
     * Init this instance.
     *
     * This will iterate over all channels and check whether all versions have been merged.
     * If not it will merge the unmerged versions.
     *
     * Note: This has to be called after the one instance is initialized.
     */
    async init() {
        MessageBus.send('debug', `[CHANNEL_INSTANCE] ChannelManager.init() called on instance: ${this.instanceId}`);
        registerCrdtAlgorithm(new LinkedListCrdtAlgorithm());
        // Register event handlers FIRST - before any async operations
        // This ensures no ChannelInfo events are missed during cache loading
        // (ObjectEventDispatcher discards events when no handler is registered)
        this.disconnectOnVersionedObjListener = objectEvents.onNewVersion(this.processNewVersion.bind(this), 'ChannelManager: processNewVersion', 'ChannelInfo');
        // Load the cache from the registry (safe - handler already registered)
        await this.loadRegistryCacheFromOne();
        MessageBus.send('debug', `[CHANNEL_INSTANCE] ChannelManager.init() completed on instance: ${this.instanceId}, cache size: ${this.channelInfoCache.size}`);
    }
    /**
     * Shutdown module
     */
    async shutdown() {
        MessageBus.send('debug', `[CHANNEL_INSTANCE] ChannelManager.shutdown() called on instance: ${this.instanceId}`);
        this.disconnectOnVersionedObjListener();
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.disconnectOnVersionedObjListener = () => { };
        this.channelInfoCache = new Map();
    }
    // ######## Channel management ########
    /**
     * Calculate the ChannelInfo ID hash for a channel with given parameters.
     *
     * This is a static helper that calculates the channel identity hash without
     * creating or accessing the channel. Use this when you need to know what
     * a channel's idHash would be (e.g., for waiting on CHUM sync).
     *
     * Channel identity = participants + discriminator + owner
     *
     * @param participantsHash - The HashGroup hash of participants
     * @param owner - The channel owner (required for channel identity)
     * @param discriminator - Optional discriminator for multi-topic support
     * @returns The SHA256IdHash that identifies this channel
     */
    static async calculateChannelInfoIdHash(participantsHash, owner, discriminator) {
        return await calculateIdHashOfObj({
            $type$: 'ChannelInfo',
            participants: participantsHash,
            ...(discriminator !== undefined && { discriminator }),
            owner
        });
    }
    /**
     * Create a new channel identified by its participants.
     *
     * If a channel with the same participants and owner already exists, this call is a noop.
     *
     * @param participants - Array of Person IDs that participate in this channel.
     * @param owner - If the owner is not passed, then your own main identity is used. If the
     * owner is NULL, then no owner is set. If a value is given, then the value will be used as
     * an owner.
     * @param existingParticipantsHash - Optional existing HashGroup to reuse
     * @param discriminator - Optional discriminator for multi-topic support
     * @param demandsTrustLevel - Optional ABAC trust level requirement ('none' for public)
     * @returns Object containing channelInfoIdHash and participantsHash
     */
    async createChannel(participants, owner, existingParticipantsHash, discriminator, demandsTrustLevel) {
        console.log(`[ChannelManager] ========== CREATE CHANNEL ==========`);
        console.log(`[ChannelManager] participants count: ${participants?.length}`);
        console.log(`[ChannelManager] participants:`, participants?.map(p => String(p).substring(0, 16)));
        console.log(`[ChannelManager] owner param: ${owner === undefined ? 'undefined' : owner === null ? 'null' : String(owner).substring(0, 16)}`);
        console.log(`[ChannelManager] existingParticipantsHash: ${existingParticipantsHash ? existingParticipantsHash.substring(0, 16) : 'none'}`);
        console.log(`[ChannelManager] discriminator: ${discriminator ?? 'none'}`);
        console.log(`[ChannelManager] demandsTrustLevel: ${demandsTrustLevel ?? 'none (default)'}`);
        // Runtime validation: participants must be an array
        if (!Array.isArray(participants)) {
            const errMsg = `createChannel called with non-array participants: ${typeof participants} = ${JSON.stringify(participants).substring(0, 100)}`;
            MessageBus.send('error', errMsg);
            console.error('[ChannelManager] ' + errMsg);
            console.error('[ChannelManager] Stack trace:', new Error().stack);
            throw new Error(errMsg);
        }
        MessageBus.send('debug', `[CHANNEL_INSTANCE] createChannel called for ${participants.length} participants on instance: ${this.instanceId}`);
        if (owner === undefined) {
            owner = await this.calculateDefaultOwner();
            console.log(`[ChannelManager] Owner was undefined, calculated default: ${owner ? String(owner).substring(0, 16) : 'null/undefined'}`);
        }
        if (owner === null) {
            throw new Error('Channel owner cannot be null. Owner is required for channel identity.');
        }
        console.log(`[ChannelManager] Final owner for channel: ${String(owner).substring(0, 16)}`);
        // Use existing HashGroup if provided, otherwise create new one
        let participantsHash;
        if (existingParticipantsHash) {
            participantsHash = existingParticipantsHash;
            // Extract participants from the existing HashGroup for access grants
            const hashGroup = await getObject(existingParticipantsHash);
            participants = hashGroup.person ? Array.from(hashGroup.person) : participants;
            console.log(`[ChannelManager] Using existing HashGroup: ${participantsHash.substring(0, 16)}, ${participants.length} participants`);
        }
        else {
            const hashGroup = {
                $type$: 'HashGroup',
                person: new Set(participants)
            };
            console.log(`[ChannelManager] Created HashGroup Set, size: ${hashGroup.person.size}`);
            console.log(`[ChannelManager] HashGroup members:`, Array.from(hashGroup.person).map(p => String(p).substring(0, 16)));
            const hashGroupResult = await storeUnversionedObject(hashGroup);
            participantsHash = hashGroupResult.hash;
            console.log(`[ChannelManager] Stored HashGroup, hash: ${participantsHash.substring(0, 16)}`);
            // Grant access to the HashGroup object itself so it can sync via CHUM
            // CRITICAL: Create INDIVIDUAL access for each person for CHUM sync to work across fresh pairings
            const { createAccess } = await import('@refinio/one.core/lib/access.js');
            const { SET_ACCESS_MODE } = await import('@refinio/one.core/lib/storage-base-common.js');
            for (const person of participants) {
                await createAccess([{
                        object: participantsHash,
                        person: [person],
                        hashGroup: [],
                        mode: SET_ACCESS_MODE.ADD
                    }]);
            }
            console.log(`[ChannelManager] Granted individual Access to HashGroup ${participantsHash.substring(0, 16)} for ${participants.length} participants`);
        }
        // Add debug logs before channel creation
        logWithParticipants(participantsHash, owner, 'createChannel - START');
        // Debug output to show cache state
        MessageBus.send('debug', `[CHANNEL] Cache state before creation - channels: ${this.channelInfoCache.size}`);
        // Display sample of channels in cache
        const channelSample = Array.from(this.channelInfoCache.entries())
            .slice(0, 5)
            .map(([hash, info]) => `${info.participants?.substring(0, 8) ?? 'null'}:${info.owner || 'null'}:${hash.substring(0, 8)}`);
        MessageBus.send('debug', `[CHANNEL] Sample channels in cache: ${JSON.stringify(channelSample)}`);
        // Calculate channel hash - identity is based on participants + discriminator + owner
        const channelInfoIdHash = await calculateIdHashOfObj({
            $type$: 'ChannelInfo',
            participants: participantsHash,
            ...(discriminator !== undefined && { discriminator }),
            owner
        });
        // Debug whether channel exists in cache
        const existsInCache = this.channelInfoCache.has(channelInfoIdHash);
        MessageBus.send('debug', `[CHANNEL] Channel exists in cache before creation: ${existsInCache}`);
        // Check if exists in storage and update cache if needed
        let existsInStorage = false;
        try {
            await getObjectByIdHash(channelInfoIdHash);
            existsInStorage = true;
            MessageBus.send('debug', `[CHANNEL] Channel exists in storage before creation: true`);
            // If channel exists in storage but not in cache, update the cache
            if (!existsInCache) {
                MessageBus.send('debug', `[CHANNEL] Channel exists in storage but not in cache, updating cache`);
                await this.addChannelIfNotExist(channelInfoIdHash);
            }
        }
        catch (error) {
            MessageBus.send('debug', `[CHANNEL] Channel exists in storage before creation: false`);
        }
        // Create channel if it doesn't exist
        if (existsInStorage) {
            logWithParticipants(participantsHash, owner, 'createChannel - END: Existed');
        }
        else {
            // Set up Promise to wait for processNewVersion to complete for this channel
            // This ensures cache is updated before we return
            const channelReady = new Promise(resolve => {
                const unsubscribe = this.onUpdated.listen((updatedIdHash) => {
                    if (updatedIdHash === channelInfoIdHash) {
                        unsubscribe();
                        resolve();
                    }
                });
            });
            // Store the channel - this triggers processNewVersion asynchronously
            await storeVersionedObject({
                $type$: 'ChannelInfo',
                participants: participantsHash,
                ...(discriminator !== undefined && { discriminator }),
                owner,
                // ABAC: Include demandsTrustLevel if specified (e.g., 'none' for public channels)
                ...(demandsTrustLevel !== undefined && { demandsTrustLevel })
            });
            // Wait for processNewVersion to complete and update cache
            await channelReady;
            logWithParticipants(participantsHash, owner, 'createChannel - END: Created');
        }
        // ALWAYS ensure IdAccess for CHUM sync - grant INDIVIDUAL access to each participant
        // CRITICAL: Create separate IdAccess for each person for CHUM sync to work across fresh pairings
        // This must run regardless of whether channel existed, to ensure proper sync
        const { createAccess } = await import('@refinio/one.core/lib/access.js');
        const { SET_ACCESS_MODE } = await import('@refinio/one.core/lib/storage-base-common.js');
        for (const person of participants) {
            await createAccess([{
                    id: channelInfoIdHash,
                    person: [person],
                    hashGroup: [],
                    mode: SET_ACCESS_MODE.ADD
                }]);
        }
        console.log(`[ChannelManager] 🔐 Created/Ensured individual IdAccess for ${channelInfoIdHash.substring(0, 16)} with ${participants.length} participants`);
        // Debug state after creation
        try {
            // Check cache state
            const existsInCacheAfter = this.channelInfoCache.has(channelInfoIdHash);
            const channelInfo = this.channelInfoCache.get(channelInfoIdHash);
            MessageBus.send('debug', `[CHANNEL] After creation - In cache: ${existsInCacheAfter}, Head: ${channelInfo?.head || 'null'}`);
            // Check storage state
            try {
                await getObjectByIdHash(channelInfoIdHash);
                MessageBus.send('debug', `[CHANNEL] After creation - In storage: true`);
                // Get head from storage if possible
                try {
                    const storedVersion = await getCurrentVersion(channelInfoIdHash);
                    MessageBus.send('debug', `[CHANNEL] After creation - Stored head: ${storedVersion.head || 'null'}`);
                }
                catch (e) {
                    MessageBus.send('debug', `[CHANNEL] Could not get stored version: ${String(e)}`);
                }
            }
            catch (error) {
                MessageBus.send('debug', `[CHANNEL] After creation - In storage: false`);
            }
        }
        catch (error) {
            MessageBus.send('debug', `[CHANNEL] Error in debug logging: ${String(error)}`);
        }
        return { channelInfoIdHash, participantsHash };
    }
    /**
     * Create a new channel with a named Group for access control.
     *
     * This method:
     * 1. Creates the channel (which creates the HashGroup internally)
     * 2. Creates a named Group referencing the same HashGroup
     * 3. Grants the Group access to the channel
     *
     * @param groupName - Name for the Group
     * @param participants - Array of Person IDs to include in the channel
     * @param owner - Channel owner (defaults to default owner)
     * @returns Object containing channelInfoIdHash, groupIdHash, and participantsHash
     */
    async createChannelWithGroup(groupName, participants, owner) {
        // 1. Create the channel (this creates the HashGroup internally)
        const { channelInfoIdHash, participantsHash } = await this.createChannel(participants, owner);
        // 2. Create named Group referencing the same HashGroup
        const group = {
            $type$: 'Group',
            name: groupName,
            hashGroup: participantsHash
        };
        const groupResult = await storeVersionedObject(group);
        // 3. Grant INDIVIDUAL access to each person for CHUM sync
        // CRITICAL: Create separate IdAccess for each person for CHUM sync to work across fresh pairings
        const { createAccess } = await import('@refinio/one.core/lib/access.js');
        const { SET_ACCESS_MODE } = await import('@refinio/one.core/lib/storage-base-common.js');
        for (const person of participants) {
            await createAccess([{
                    id: channelInfoIdHash,
                    person: [person],
                    hashGroup: [],
                    mode: SET_ACCESS_MODE.ADD
                }]);
        }
        MessageBus.send('log', `Created channel with Group '${groupName}' (${participants.length} participants, individual access)`);
        return {
            channelInfoIdHash,
            groupIdHash: groupResult.idHash,
            participantsHash
        };
    }
    /**
     * Retrieve all channels registered at the channel registry
     *
     * @param options
     * @returns
     */
    async channels(options) {
        const channelInfos = await this.getMatchingChannelInfos(options);
        return channelInfos.map(info => {
            return {
                participants: info.participants,
                discriminator: info.discriminator,
                owner: info.owner
            };
        });
    }
    /**
     * Check if passed channel exists.
     *
     * @param participants - The participants hash identifying the channel
     * @param owner
     */
    async hasChannel(participants, owner) {
        return (await this.channels({ participants, owner })).length > 0;
    }
    /**
     * Enable appending the default profile of the sender to each channel entry.
     *
     * Default is disabled
     *
     * @param channel
     * @param enable
     */
    setChannelSettingsAppendSenderProfile(channel, enable) {
        const currentSettings = this.channelSettings.get(channel);
        if (currentSettings) {
            if (enable === undefined) {
                delete currentSettings.appendSenderProfile;
            }
            else {
                currentSettings.appendSenderProfile = enable;
            }
        }
        else if (enable !== undefined) {
            this.channelSettings.set(channel, {
                appendSenderProfile: enable
            });
        }
    }
    /**
     * Get the "AppendSenderProfile" setting for the specified channel.
     *
     * @param channel
     */
    getChannelSettingsAppendSenderProfile(channel) {
        const currentSettings = this.channelSettings.get(channel);
        if (currentSettings === undefined || currentSettings.appendSenderProfile === undefined) {
            return false;
        }
        return currentSettings.appendSenderProfile;
    }
    /**
     * Enable registering as metadata attached profiles with leute.
     *
     * Default is disabled
     *
     * @param channel
     * @param enable
     */
    setChannelSettingsRegisterSenderProfileAtLeute(channel, enable) {
        const currentSettings = this.channelSettings.get(channel);
        if (currentSettings) {
            if (enable === undefined) {
                delete currentSettings.registerSenderProfileAtLeute;
            }
            else {
                currentSettings.registerSenderProfileAtLeute = enable;
            }
        }
        else if (enable !== undefined) {
            this.channelSettings.set(channel, {
                registerSenderProfileAtLeute: enable
            });
        }
    }
    /**
     * Get the "RegisterSenderProfileAtLeute" setting for the specified channel.
     *
     * @param channel
     */
    getChannelSettingsRegisterSenderProfileAtLeute(channel) {
        const currentSettings = this.channelSettings.get(channel);
        if (currentSettings === undefined ||
            currentSettings.registerSenderProfileAtLeute === undefined) {
            return false;
        }
        return currentSettings.registerSenderProfileAtLeute;
    }
    /**
     * If size is specified, then restrict the size of a channel to this amount.
     *
     * Excess data is deleted by the merge algorithms.
     *
     * @param channel
     * @param maxSize
     */
    setChannelSettingsMaxSize(channel, maxSize) {
        if (maxSize !== undefined && maxSize < 0) {
            throw new Error('Max size must not be negative');
        }
        const currentSettings = this.channelSettings.get(channel);
        if (currentSettings) {
            if (maxSize === undefined) {
                delete currentSettings.maxSize;
            }
            else {
                currentSettings.maxSize = maxSize;
            }
        }
        else if (maxSize !== undefined) {
            this.channelSettings.set(channel, {
                maxSize
            });
        }
    }
    /**
     * Get the "maxSize" setting for the specified channel.
     *
     * @param channel
     */
    getChannelSettingsMaxSize(channel) {
        const currentSettings = this.channelSettings.get(channel);
        if (currentSettings === undefined || currentSettings.maxSize === undefined) {
            return undefined;
        }
        return currentSettings.maxSize;
    }
    // ######## Put data into the channel ########
    /**
     * Post a new object to a channel.
     *
     * @param participants - The participants hash identifying the channel
     * @param data - The object to post to the channel
     * @param channelOwner - If the owner is not passed, then your own main identity is used.
     * If the owner is NULL, then no owner is set. If a value is given, then the value will be used as
     * an owner.
     * @param timestamp
     * @param author
     * @param discriminator - Optional discriminator for multi-topic support
     */
    async postToChannel(participants, data, channelOwner, timestamp, author, discriminator) {
        MessageBus.send('debug', `[CHANNEL_INSTANCE] postToChannel called for participants ${participants.substring(0, 8)} on instance: ${this.instanceId}`);
        // Determine the owner to use for posting.
        // Each participant posts to their own owner channel for encryption/offline support.
        // Reading merges all owner channels via multiChannelObjectIterator.
        const owner = channelOwner ?? await this.calculateDefaultOwner();
        if (author === undefined) {
            author = owner;
        }
        // Channel identity is based on participants + discriminator + owner
        const channelInfoIdHash = await calculateIdHashOfObj({
            $type$: 'ChannelInfo',
            participants: participants,
            ...(discriminator !== undefined && { discriminator }),
            owner
        });
        // Setup the merge handler
        let cacheEntry = this.channelInfoCache.get(channelInfoIdHash);
        if (!cacheEntry) {
            // Before throwing an error, check if the channel exists in storage but not in cache
            try {
                await getObjectByIdHash(channelInfoIdHash);
                // If we reach here, channel exists in storage but not in cache
                MessageBus.send('debug', `[CHANNEL] Channel exists in storage but not in cache during post, updating cache`);
                await this.addChannelIfNotExist(channelInfoIdHash);
                // Try again with the updated cache
                cacheEntry = this.channelInfoCache.get(channelInfoIdHash);
                if (!cacheEntry) {
                    throw new Error('Failed to update cache with existing channel');
                }
            }
            catch (error) {
                // Channel doesn't exist - auto-create it for group chats (when owner is set)
                if (owner !== undefined) {
                    MessageBus.send('debug', `[CHANNEL] Auto-creating channel for owner ${String(owner).substring(0, 16)} with participants ${String(participants).substring(0, 16)}`);
                    // Pass existing participantsHash to ensure IdAccess uses the same HashGroup
                    await this.createChannel([...await this.getParticipantsFromHash(participants)], owner, participants, // Use original HashGroup for IdAccess
                    discriminator);
                    cacheEntry = this.channelInfoCache.get(channelInfoIdHash);
                    if (!cacheEntry) {
                        throw new Error('Failed to create channel for posting');
                    }
                }
                else {
                    throw new Error('This channel does not exist, you cannot post to it.');
                }
            }
        }
        // Post the data
        try {
            await serializeWithType(ChannelManager.postLockName, async () => {
                logWithParticipants(participants, owner, 'postToChannel - START');
                // Debug the state of the channel before posting
                const channelBeforePost = this.channelInfoCache.get(channelInfoIdHash);
                MessageBus.send('debug', `[CHANNEL_POST] Before posting to channel ${participants.substring(0, 8)} - head: ${channelBeforePost?.head || 'null'}`);
                await this.internalChannelPost(participants, owner, data, author, timestamp, discriminator);
                // Debug the state of the channel after posting
                const channelAfterPost = this.channelInfoCache.get(channelInfoIdHash);
                MessageBus.send('debug', `[CHANNEL_POST] After posting to channel ${participants.substring(0, 8)} - head: ${channelAfterPost?.head || 'null'}`);
                // Verify the channel info was properly updated in storage
                try {
                    const storedVersion = await getCurrentVersion(channelInfoIdHash);
                    MessageBus.send('debug', `[CHANNEL_POST] Stored version after post - head: ${storedVersion.head || 'null'}`);
                    // Detect and fix head pointer mismatch
                    if (channelAfterPost?.head !== storedVersion.head) {
                        MessageBus.send('debug', `[CHANNEL_POST] Head pointer mismatch detected - cache: ${channelAfterPost?.head}, storage: ${storedVersion.head}`);
                    }
                }
                catch (e) {
                    MessageBus.send('debug', `[CHANNEL_POST] Error verifying storage after post: ${String(e)}`);
                }
                // Emit onUpdated event synchronously after posting
                // ONLY when ObjectEventDispatcher is NOT initialized (browser platform)
                // When ObjectEventDispatcher IS initialized (Electron), processNewVersion will emit
                // Emitting in both cases causes duplicate AI responses
                if (!objectEvents.isInitialized()) {
                    try {
                        console.log(`[CHANNEL_POST] 🔍 About to get latest channel info for ${participants.substring(0, 8)} (sync emit - no ObjectEventDispatcher)`);
                        const latestChannelInfo = await getCurrentVersion(channelInfoIdHash);
                        const latestEntry = latestChannelInfo.head;
                        console.log(`[CHANNEL_POST] 🔍 latestEntry = ${latestEntry ? String(latestEntry).substring(0, 16) + '...' : 'NULL'}`);
                        if (latestEntry) {
                            const entry = (await getObject(latestEntry));
                            console.log(`[CHANNEL_POST] ✅ Emitting onUpdated for ${participants.substring(0, 8)}`);
                            this.onUpdated.emit(channelInfoIdHash, participants, owner || null, new Date(entry.creationTime), [{ ...entry, isNew: true }]);
                        }
                        else {
                            console.log(`[CHANNEL_POST] ❌ latestEntry is null - cannot emit onUpdated for ${participants.substring(0, 8)}`);
                        }
                    }
                    catch (e) {
                        console.log(`[CHANNEL_POST] ❌ Error emitting onUpdated: ${String(e)}`);
                    }
                }
                else {
                    console.log(`[CHANNEL_POST] ⏭️  Skipping sync emit - ObjectEventDispatcher will handle via processNewVersion`);
                }
                logWithParticipants(participants, owner, 'postToChannel - END');
            });
        }
        catch (e) {
            logWithParticipants(participants, owner, `postToChannel - FAIL: ${String(e)}`);
            throw e;
        }
    }
    /**
     * Post a new object to a channel but only if it was not already posted to the channel
     *
     * Note: This will iterate over the whole tree if the object does not exist, so it might
     *       be slow.
     *
     * @param participants - The participants hash identifying the channel
     * @param data - The object to post to the channel
     * @param channelOwner - If the owner it's not passed, then the {@link this.defaultOwner} is
     * set. If the owner it's NULL, then no owner is set. If a value is given, then the value
     * will be used as an owner.
     * @param discriminator - Optional discriminator for multi-topic support
     */
    async postToChannelIfNotExist(participants, data, channelOwner, discriminator) {
        // Determine the owner to use for posting.
        // Each participant posts to their own owner channel for encryption/offline support.
        const owner = channelOwner ?? await this.calculateDefaultOwner();
        try {
            // We need to serialize here, because two posts of the same item must be serialized
            // in order for the second one to wait until the first one was inserted.
            await serializeWithType(ChannelManager.postNELockName, async () => {
                logWithParticipants(participants, owner, 'postToChannelIfNotExist - START');
                // Calculate the hash of the passed object. We will compare it with existing entries
                const dataHash = await calculateHashOfObj(data);
                // Iterate over the channel to see whether the object exists.
                let exists = false;
                for await (const item of this.objectIterator({ participants, owner, discriminator })) {
                    if (item.dataHash === dataHash) {
                        exists = true;
                    }
                }
                // Post only if it does not exist
                if (exists) {
                    logWithParticipants(participants, owner, 'postToChannelIfNotExist - END: existed');
                }
                else {
                    await this.postToChannel(participants, data, owner, undefined, undefined, discriminator);
                    logWithParticipants(participants, owner, 'postToChannelIfNotExist - END: posted');
                }
            });
        }
        catch (e) {
            logWithParticipants(participants, owner, `postToChannelIfNotExist - FAIL: ${String(e)}`);
            throw e;
        }
    }
    // ######## Get data from channels - Array based ########
    /**
     * Get all data from one or multiple channels.
     *
     * Note the behavior when using ascending ordering (default) and count.
     * It will return the 'count' latest elements in ascending order, not the
     * 'count' oldest elements. It is counter intuitive and should either
     * be fixed or the iterator interface should be the mandatory
     *
     * @param queryOptions
     */
    async getObjects(queryOptions) {
        // Use iterator interface to collect all objects
        const objects = [];
        for await (const obj of this.objectIterator(queryOptions)) {
            objects.push(obj);
        }
        // Decide, whether to return it reversed, or not
        if (queryOptions && queryOptions.orderBy === Order.Descending) {
            return objects;
        }
        else {
            return objects.reverse();
        }
    }
    /**
     * Get all data from a channel.
     *
     * @param type - Type of objects to retrieve. If type does not match the object is skipped.
     * @param queryOptions
     */
    async getObjectsWithType(type, queryOptions) {
        // Use iterator interface to collect all objects
        const objects = [];
        const start = Date.now();
        let iterCount = 0;
        for await (const obj of this.objectIteratorWithType(type, queryOptions)) {
            objects.push(obj);
            iterCount++;
        }
        console.log(`[ChannelManager] getObjectsWithType(${type}): ${iterCount} objects in ${Date.now() - start}ms`);
        // Decide, whether to return it reversed, or not
        if (queryOptions && queryOptions.orderBy === Order.Descending) {
            return objects;
        }
        else {
            return objects.reverse();
        }
    }
    /**
     * Obtain a specific object from a channel.
     *
     * @param id - id of the object to extract
     */
    async getObjectById(id) {
        const obj = (await this.objectIterator({ id }).next()).value;
        if (!obj) {
            throw new Error('The referenced object does not exist');
        }
        return obj;
    }
    /**
     * Obtain a specific object from a channel.
     *
     * This is a very inefficient implementation, because it iterates over the chain.
     * In the future it would be better to just pick the object with the passed hash.
     * But this only works when we have working reverse maps for getting the metadata.
     * The other option would be to use the hash of the indexed metadata as id, then
     * we don't have the reverse map problem.
     *
     * @param id - id of the object to extract
     * @param type - Type of objects to retrieve. If type does not match an
     *               error is thrown.
     * @returns
     */
    async getObjectWithTypeById(id, type) {
        function hasRequestedType(obj) {
            return obj.data.$type$ === type;
        }
        const obj = (await this.objectIterator({ id }).next()).value;
        if (!obj) {
            throw new Error('The referenced object does not exist');
        }
        if (!hasRequestedType(obj)) {
            throw new Error(`The referenced object does not have the expected type ${type}`);
        }
        return obj;
    }
    // ######## Get data from channels - ITERATORS ########
    /**
     * Iterate over all objects in the channels matching the query options.
     *
     * Note that the sort order is not supported. It is silently ignored.
     * Items are always returned in descending order regarding time.
     * It is a single linked list underneath, so no way of efficiently iterating
     * in the other direction.
     *
     * @param queryOptions
     * @returns
     */
    async *objectIterator(queryOptions) {
        // The count needs to be dealt with at the top level, because it involves all returned items
        if (queryOptions && queryOptions.count) {
            let elementCounter = 0;
            // Iterate over the merge iterator and filter unwanted elements
            for await (const element of this.multiChannelObjectIterator(queryOptions)) {
                if (queryOptions.count !== undefined && elementCounter >= queryOptions.count) {
                    break;
                }
                ++elementCounter;
                yield element;
            }
        }
        else {
            yield* this.multiChannelObjectIterator(queryOptions);
        }
    }
    /**
     * Iterate over all objects in the channels matching the query options.
     *
     * This method also returns only the objects of a certain type.
     *
     * @param type - The type of the elements to iterate
     * @param queryOptions
     * @returns
     */
    async *objectIteratorWithType(type, queryOptions) {
        if (queryOptions) {
            queryOptions.type = type;
        }
        else {
            queryOptions = { type };
        }
        // Iterate over all objects filtering out the ones with the wrong type
        yield* this.objectIterator(queryOptions);
    }
    /**
     * Find the differences in the chain starting from the common history
     *
     * Note: this only works when both channel infos are from the same channel.
     *
     * @param nextChannel
     * @param currentChannel
     */
    static async *differencesIteratorMostCurrent(nextChannel, currentChannel) {
        const channelInfoNext = await getObject(nextChannel);
        const channelInfoCurrent = await getObject(currentChannel);
        const itNext = ChannelManager.singleChannelObjectIterator(channelInfoNext);
        const itCurrent = ChannelManager.singleChannelObjectIterator(channelInfoCurrent);
        if (!channelInfoNext.head) {
            yield* itCurrent;
        }
        else if (channelInfoCurrent.head === undefined) {
            yield* itNext;
        }
        else {
            yield* ChannelManager.mergeIteratorMostCurrent([itNext, itCurrent], true, false, true);
        }
    }
    // ######## Get data from channels - ITERATORS PRIVATE ########
    /**
     * Iterate over all objects in the channels selected by the passed ChannelSelectionOptions.
     *
     * @param queryOptions
     * @returns
     */
    async *multiChannelObjectIterator(queryOptions) {
        const channelStart = Date.now();
        const channels = await this.getMatchingChannelInfos(queryOptions);
        console.log(`[ChannelManager] multiChannelObjectIterator: ${channels.length} channels found in ${Date.now() - channelStart}ms`);
        // prepare the options for the single channel iterator
        let from;
        let to;
        let ids;
        let types;
        let omitData = false;
        let omitSharedWith = true;
        if (queryOptions) {
            from = queryOptions.from;
            to = queryOptions.to;
            if (queryOptions.id) {
                ids = [queryOptions.id];
            }
            if (queryOptions.ids) {
                ids = queryOptions.ids;
            }
            if (queryOptions.type) {
                types = [queryOptions.type];
            }
            if (queryOptions.types) {
                types = queryOptions.types;
            }
            if (queryOptions.omitData) {
                omitData = queryOptions.omitData;
            }
            if (queryOptions.omitSharedWith) {
                omitSharedWith = queryOptions.omitSharedWith;
            }
        }
        // Create a iterator for each selected channel
        const iterators = channels.map(channel => ChannelManager.singleChannelObjectIterator(channel, from, to, ids, true));
        // Determine the access rights of each channel
        const sharedWithPersonsMap = new Map();
        if (!omitSharedWith) {
            await Promise.all(channels.map(async (channel) => {
                const channelInfoIdHash = await calculateIdHashOfObj(channel);
                const sharedWithPersons = await ChannelManager.sharedWithPersonsList(channelInfoIdHash);
                sharedWithPersonsMap.set(channelInfoIdHash, sharedWithPersons);
            }));
        }
        // Iterate over all channels and fetch the data
        for await (const entry of ChannelManager.mergeIteratorMostCurrent(iterators, false)) {
            const result = await this.convertRawChannelEntryToObjectData(entry, sharedWithPersonsMap, omitData);
            if (result.data && types && !types.includes(result.data.$type$)) {
                continue;
            }
            yield result;
        }
    }
    /**
     * Converts the raw represantation to ObjectData<T> representation.
     *
     * @param entry
     * @param sharedWithPersonsMap
     * @param omitData
     */
    async convertRawChannelEntryToObjectData(entry, sharedWithPersonsMap, omitData) {
        // Get the shared with status from the precompiled map
        const sharedWith = sharedWithPersonsMap.get(entry.channelInfoIdHash) || [];
        // Load the object to compare the type
        // AUTHORIZED HACK - casting undefined to any type because
        // making the data field optional would cause problems
        // in other apps.
        const data = omitData ? undefined : await getObject(entry.dataHash);
        // Build meta data object and return it
        return {
            channelParticipants: entry.channelInfo.participants,
            channelOwner: entry.channelInfo.owner,
            channelEntryHash: entry.channelEntryHash,
            id: ChannelManager.encodeEntryId(entry.channelInfoIdHash, entry.channelEntryHash),
            creationTime: new Date(entry.creationTime),
            creationTimeHash: entry.creationTimeHash,
            author: entry.author,
            sharedWith: sharedWith,
            data: data,
            dataHash: entry.dataHash
        };
    }
    /**
     * This iterator just iterates the data elements of the passed channel.
     *
     * Note: If you want to start iterating from a specific point in the chain
     * and not from the start, you can just construct your own ChannelInfo object
     * and set the head to the ChannelEntry where you want to start iterating.
     *
     * @param channelInfo - iterate this channel
     * @param from
     * @param to
     * @param loadAuthor
     * @returns
     */
    static async *singleChannelObjectIterator(channelInfo, from, to, ids, loadAuthor) {
        logWithParticipants(channelInfo.participants, channelInfo.owner, 'singleChannelObjectIterator - ENTER');
        // Select the item or entry iterator based on whether ids were passed
        if (ids) {
            yield* this.itemIterator(channelInfo, ids, from, to, loadAuthor);
        }
        else {
            yield* this.entryIterator(channelInfo, from, to, loadAuthor);
        }
        logWithParticipants(channelInfo.participants, channelInfo.owner, 'singleChannelObjectIterator - LEAVE: exhausted entries');
    }
    /**
     * This iterator just iterates the data elements of the passed channel.
     *
     * Note: If you want to start iterating from a specific point in the chain
     * and not from the start, you can just construct your own ChannelInfo object
     * and set the head to the ChannelEntry where you want to start iterating.
     *
     * @param channelInfo - iterate this channel
     * @param from
     * @param to
     * @param loadAuthor
     * @returns
     */
    static async *entryIterator(channelInfo, from, to, loadAuthor) {
        logWithParticipants(channelInfo.participants, channelInfo.owner, 'entryIterator - ENTER');
        // Calculate the channel hash first so we can include it in error messages
        const channelInfoIdHash = await calculateIdHashOfObj(channelInfo);
        if (!channelInfo.head) {
            logWithParticipants(channelInfo.participants, channelInfo.owner, 'entryIterator - LEAVE: no entries');
            MessageBus.send('debug', `[CHANNEL_CONTENT] Channel ${channelInfo.participants.substring(0, 8)} has no head pointer, cannot iterate content - channel hash: ${channelInfoIdHash}`);
            return;
        }
        MessageBus.send('debug', `[CHANNEL_CONTENT] Starting iteration of channel ${channelInfo.participants.substring(0, 8)} with head: ${channelInfo.head} - channel hash: ${channelInfoIdHash}`);
        let entryCount = 0;
        // Iterate over all elements and yield each element
        for await (const entry of linkedListIterator(channelInfo.head)) {
            entryCount++;
            const currentEntryHash = entry.linkedListEntryHash;
            logWithParticipants_Debug(channelInfo.participants, channelInfo.owner, `entryIterator: iterate ${entry.linkedListEntryHash}`);
            // Filter elements based on from / to
            if (from && entry.creationTime < from.getTime()) {
                break;
            }
            if (to && entry.creationTime > to.getTime()) {
                continue;
            }
            let author;
            if (loadAuthor && entry.metaDataHashes !== undefined) {
                for (const metaHash of entry.metaDataHashes) {
                    const metaObject = await getObject(metaHash);
                    if (metaObject.$type$ === 'Signature') {
                        const certObject = await getObject(metaObject.data);
                        if (certObject.$type$ === 'AffirmationCertificate') {
                            author = metaObject.issuer;
                            break;
                        }
                    }
                }
            }
            yield {
                channelInfo: channelInfo,
                channelInfoIdHash: channelInfoIdHash,
                channelEntryHash: currentEntryHash,
                creationTimeHash: entry.creationTimeHash,
                creationTime: entry.creationTime,
                dataHash: entry.dataHash,
                metaDataHashes: entry.metaDataHashes,
                author
            };
        }
        MessageBus.send('debug', `[CHANNEL_CONTENT] Finished iterating channel ${channelInfo.participants.substring(0, 8)}, found ${entryCount} entries`);
        logWithParticipants(channelInfo.participants, channelInfo.owner, 'entryIterator - LEAVE: exhausted entries');
    }
    /**
     * This iterator just iterates over the elements with the passed ids.
     *
     * @param channelInfo
     * @param ids
     * @param from
     * @param to
     * @param loadAuthor
     * @returns
     */
    static async *itemIterator(channelInfo, ids, from, to, loadAuthor) {
        logWithParticipants(channelInfo.participants, channelInfo.owner, 'itemIterator - ENTER');
        // Calculate the id hash
        const channelInfoIdHash = await calculateIdHashOfObj(channelInfo);
        // Extract the items for which we have the id
        const entries = [];
        for (const id of ids) {
            const entryData = ChannelManager.decodeEntryId(id);
            if (entryData.channelInfoIdHash !== channelInfoIdHash) {
                continue;
            }
            const entry = await getObject(entryData.channelEntryHash);
            const creationTimeHash = entry.data;
            const creationTime = await getObject(creationTimeHash);
            if (to && creationTime.timestamp > to.getTime()) {
                continue;
            }
            if (from && creationTime.timestamp < from.getTime()) {
                continue;
            }
            let author;
            if (loadAuthor && entry.metadata !== undefined) {
                for (const metaHash of entry.metadata) {
                    const metaObject = await getObject(metaHash);
                    if (metaObject.$type$ === 'Signature') {
                        const certObject = await getObject(metaObject.data);
                        if (certObject.$type$ === 'AffirmationCertificate') {
                            author = metaObject.issuer;
                            break;
                        }
                    }
                }
            }
            entries.push({
                channelInfo: channelInfo,
                channelInfoIdHash: channelInfoIdHash,
                channelEntryHash: entryData.channelEntryHash,
                creationTimeHash: creationTimeHash,
                creationTime: creationTime.timestamp,
                dataHash: creationTime.data,
                author
            });
        }
        // Sort the items
        entries.sort((a, b) => b.creationTime - a.creationTime);
        // yield the items
        yield* entries;
        logWithParticipants(channelInfo.participants, channelInfo.owner, 'itemIterator - LEAVE: exhausted entries');
    }
    /**
     * Iterate multiple iterators by returning always the most current element of all of them.
     *
     * It is assumed, that the iterators will return the elements sorted from highest to
     * lowest value.
     *
     * Example:
     *
     * If you have multiple iterators (iter1, iter2, iter3) that would return these items:
     * - iter1: 9, 5, 3
     * - iter2: 8, 7, 6, 1
     * - iter3: 4, 2
     *
     * Then this iterator implementation would return the items with these creation times:
     * 9, 8, 7, 6, 5, 4, 3, 2, 1
     *
     * When two or more iterators reach the same history, then the first iterator in the iterator
     * list will continue iterating. The other iterators will stop. This is relevant if one
     * iterator is faster than the other (because one iterator iterates over cached values
     * instead of one objects -> e.g. an element cache in ui elements.)
     *
     * @param  iterators
     * @param terminateOnSingleIterator - If true, then stop iteration when all but
     * one iterator reached their end. The first element of the last iterator is still returned,
     * but then iteration stops. This is very useful for merging algorithms, because they can
     * use the last item as common history for merging. Because this iteration also removes
     * redundant iterators (that iterate over the same history) it will stop when multiple
     * iterators iterate the same history.
     * @param yieldCommonHistoryElement - If true (default) the common history element
     * will be yielded as last element
     * @param onlyDifferentElements - If true (default false) only elements that are only in a
     * single channel are yielded.
     * @returns the RawChannelEntry, iterIndex (the index of the iterator in the iterators
     * array that yielded this RawChannelEntry), activeIteratorCount (number of iterators that
     * were active when this element was yielded)
     */
    static async *mergeIteratorMostCurrent(iterators, terminateOnSingleIterator = false, yieldCommonHistoryElement = true, onlyDifferentElements = false) {
        logWithParticipants(null, null, `mergeIteratorMostCurrent - ENTER: ${iterators.length} iterators`);
        // This array holds the topmost value of each iterator
        // The position of the element in this array matches the position in the iterators array.
        // Those values are then compared and the one with the highest
        // timestamp is returned and then replaced by the next one on each iteration
        const currentValues = [];
        let previousItem = undefined;
        // Initial fill of the currentValues iterator with the most current elements of each iterator
        for (const iterator of iterators) {
            currentValues.push((await iterator.next()).value);
        }
        // Iterate over all (output) items
        // The number of the iterations will be the sum of all items returned by all iterators.
        // For the above example it would be 9 iterations.
        while (true) {
            // determine the largest element in currentValues
            let mostCurrentItem = undefined;
            let mostCurrentIndex = 0;
            let activeIteratorCount = 0;
            for (let i = 0; i < currentValues.length; i++) {
                const currentValue = currentValues[i];
                // Ignore values from iterators that have reached their end (returned undefined)
                if (currentValue === undefined) {
                    continue;
                }
                else {
                    ++activeIteratorCount;
                }
                // This checks whether we have an element to compare to (so i is at least 1)
                if (mostCurrentItem) {
                    // Skip elements that are older (less current)
                    if (currentValue.creationTime < mostCurrentItem.creationTime) {
                        continue;
                    }
                    // If the timestamp is equal, then sort by time hash to have a predictable order
                    if (currentValue.creationTime === mostCurrentItem.creationTime &&
                        currentValue.creationTimeHash < mostCurrentItem.creationTimeHash) {
                        continue;
                    }
                    // Ignore elements with the same history (same channel id and same entry =>
                    // history is the same)
                    // This is mostly required if we mergeIterate multiple versions of the same
                    // channel. The merge algorithm uses this.
                    if (currentValue.creationTime === mostCurrentItem.creationTime &&
                        currentValue.channelEntryHash === mostCurrentItem.channelEntryHash &&
                        currentValue.channelInfoIdHash === mostCurrentItem.channelInfoIdHash) {
                        // This removes the current element from the currentValues list
                        // Thus the corresponding iterator will never be advanced again, so
                        // we effectively removed the duplicate history from the iteration
                        currentValues[i] = undefined;
                        --activeIteratorCount;
                        continue;
                    }
                }
                // If we made it to here, then we have a larger element - remember it
                mostCurrentItem = currentValues[i];
                mostCurrentIndex = i;
            }
            // If no element was found, this means that all iterators reached their ends =>
            // terminate the loop
            if (mostCurrentItem === undefined) {
                break;
            }
            // For only different elements option we call next for all equal elements and if we
            // have the same elements multiple times we don't yield.
            if (onlyDifferentElements) {
                // Same get the indices of the currentValues that are equal to the most current
                // element
                const sameIndices = [];
                for (let i = 0; i < currentValues.length; i++) {
                    const currentValue = currentValues[i];
                    // Ignore values from iterators that have reached their end (returned undefined)
                    if (currentValue === undefined) {
                        continue;
                    }
                    if (currentValue.creationTimeHash === mostCurrentItem.creationTimeHash &&
                        currentValue.channelInfoIdHash === mostCurrentItem.channelInfoIdHash) {
                        sameIndices.push(i);
                    }
                }
                // Advance all equal element iterators
                for (const index of sameIndices) {
                    currentValues[index] = (await iterators[index].next()).value;
                }
                // If we don't advanced all iterators, then it is a difference, because one channel
                // is missing this element.
                if (sameIndices.length === iterators.length) {
                    continue;
                }
            }
            else {
                // Advance the iterator that yielded the highest creationTime
                currentValues[mostCurrentIndex] = (await iterators[mostCurrentIndex].next()).value;
            }
            // If we have one active iterator remaining and the user requested it, we terminate
            // This is done before the yield, because we want the first element of the remaining
            // iterator not to be returned.
            if (terminateOnSingleIterator &&
                !yieldCommonHistoryElement &&
                activeIteratorCount === 1) {
                break;
            }
            // Filter for duplicates
            if (previousItem &&
                previousItem.creationTime === mostCurrentItem.creationTime &&
                previousItem.creationTimeHash === mostCurrentItem.creationTimeHash &&
                previousItem.channelInfoIdHash === mostCurrentItem.channelInfoIdHash) {
                logWithParticipants_Debug(null, null, `mergeIteratorMostCurrent: skipped value from iterator ${mostCurrentIndex}: duplicate with previous`);
            }
            else {
                logWithParticipants_Debug(null, null, `mergeIteratorMostCurrent: picked value from iterator ${mostCurrentIndex}`);
                // Yield the value that has the highest creationTime
                yield {
                    ...mostCurrentItem,
                    iterIndex: mostCurrentIndex,
                    activeIteratorCount
                };
                // If we have one active iterator remaining and the user requested it, we terminate
                // This is done after the yield, because we want the first element of the remaining
                // iterator to be returned.
                if (terminateOnSingleIterator &&
                    yieldCommonHistoryElement &&
                    activeIteratorCount === 1) {
                    break;
                }
            }
            previousItem = mostCurrentItem;
        }
        logWithParticipants(null, null, 'mergeIteratorMostCurrent - LEAVE');
    }
    // ######## Entry id and channel selection stuff ########
    /**
     * Encodes an entry as string for referencing and loading it later.
     *
     * @param channelInfoIdHash
     * @param channelEntryHash
     * @returns
     */
    static encodeEntryId(channelInfoIdHash, channelEntryHash) {
        return `${channelInfoIdHash}_${channelEntryHash}`;
    }
    /**
     * Decodes the string identifying an entry.
     *
     * @param id
     * @returns
     */
    static decodeEntryId(id) {
        const idElements = id.split('_');
        if (idElements.length !== 2) {
            throw new Error('Id of channel entry is not valid.');
        }
        return {
            channelInfoIdHash: ensureIdHash(idElements[0]),
            channelEntryHash: ensureHash(idElements[1])
        };
    }
    /**
     * This returns the list of matching channel infos based on ChannelSelectionOptions.
     *
     * It usually returns the channel infos of the latest merged versions, not the latest
     * version in the version maps. Only if the ChannelSelectionOptions reference a specific
     * version this version is returned instead of the latest merged one.
     * @param options
     * @returns
     */
    async getMatchingChannelInfos(options) {
        MessageBus.send('debug', `[CHANNEL_INSTANCE] getMatchingChannelInfos called on instance: ${this.instanceId}, options: ${JSON.stringify(options)}`);
        logWithParticipants(null, null, `getMatchingChannelInfos - START: ${JSON.stringify(options)}`);
        // #### Check parameters ####
        if (options && options.participants && options.participantsList) {
            throw new Error("You cannot specify 'participants' and 'participantsList' at the same time in query options!");
        }
        if (options && options.owner !== undefined && options.owners) {
            throw new Error("You cannot specify 'owner' and 'owners' at the same time in query options!");
        }
        if (options && options.channel && options.channels) {
            throw new Error("You cannot specify 'channel' and 'channels' at the same time in query options!");
        }
        if (options && options.id && options.ids) {
            throw new Error("You cannot specify 'id' and 'ids' at the same time in query options!");
        }
        if (options && options.channelInfoHash && options.channelInfoHashes) {
            throw new Error("You cannot specify 'channelInfoHash' and 'channelInfoHashes' at the same time in query options!");
        }
        if (options && options.channelInfoIdHash && options.channelInfoIdHashes) {
            throw new Error("You cannot specify 'channelInfoHash' and 'channelInfoHashes' at the same time in query options!");
        }
        // #### Map parameters ####
        // Map options.participants to a single variable
        let participantsList = null;
        if (options && options.participants) {
            participantsList = [options.participants];
        }
        if (options && options.participantsList) {
            participantsList = options.participantsList;
        }
        // Map options.owner(s) to a single variable
        let owners = null;
        if (options && options.owner !== undefined) {
            if (options.owner === 'mainId') {
                owners = [await this.leuteModel.myMainIdentity()];
            }
            else {
                owners = [options.owner];
            }
        }
        if (options && options.owners) {
            owners = [];
            for (const owner of options.owners) {
                if (owner === 'mainId') {
                    owners.push(await this.leuteModel.myMainIdentity());
                }
                else {
                    owners.push(owner);
                }
            }
        }
        // Map options.channel(s) to a single variable
        let channels = null;
        if (options && options.channel) {
            channels = [options.channel];
        }
        if (options && options.channels) {
            channels = options.channels;
        }
        // Map options.id(s) to a single variable
        let ids = null;
        if (options && options.id) {
            ids = [options.id];
        }
        if (options && options.ids) {
            ids = options.ids;
        }
        // Map options.channelInfoHash(es) to a single variable
        let channelInfoHashes = null;
        if (options && options.channelInfoHash) {
            channelInfoHashes = [options.channelInfoHash];
        }
        if (options && options.channelInfoHashes) {
            channelInfoHashes = options.channelInfoHashes;
        }
        // Map options.channelInfoIdHash(es) to a single variable
        let channelInfoIdHashes = null;
        if (options && options.channelInfoIdHash) {
            channelInfoIdHashes = [options.channelInfoIdHash];
        }
        if (options && options.channelInfoIdHashes) {
            channelInfoIdHashes = options.channelInfoIdHashes;
        }
        // #### Get channel ids / infos from parameters ####
        // Variables will be filled with all channels that need to be selected
        // At the end the id hashes will also be appended to the ChannelInfos
        const selectedChannelInfos = [];
        const selectedChannelIdHashes = [];
        // Channel selection based on owner / participants / discriminator
        // It is an AND relation, so all criteria have to match in order for the channel to
        // be selected
        if (owners || participantsList || options?.discriminator !== undefined) {
            for (const channelInfo of this.channelInfoCache.values()) {
                if (participantsList && !participantsList.includes(channelInfo.participants)) {
                    continue;
                }
                if (owners &&
                    !owners.includes(channelInfo.owner === undefined ? null : channelInfo.owner)) {
                    continue;
                }
                // Filter by discriminator if specified
                if (options?.discriminator !== undefined && channelInfo.discriminator !== options.discriminator) {
                    continue;
                }
                selectedChannelInfos.push(channelInfo);
            }
        }
        // CRITICAL: Discover ALL owner channels from storage
        // Multiple owners can have channels with same participants+discriminator (same ID hash).
        // We need to iterate ALL version nodes to find each owner's channel, not just getCurrentVersion().
        if (participantsList) {
            for (const participantsHash of participantsList) {
                try {
                    // Get the HashGroup to know all potential owners
                    const hashGroup = await getObject(participantsHash);
                    const participants = hashGroup.person ? Array.from(hashGroup.person) : [];
                    // Query storage for ChannelInfo objects referencing this HashGroup
                    const channelInfoHashes = await getAllEntries(participantsHash, 'ChannelInfo');
                    // Track which owners we've already found for this participants+discriminator combo
                    const foundOwners = new Set();
                    // First, note which owners are already in selectedChannelInfos
                    for (const existing of selectedChannelInfos) {
                        if (existing.participants === participantsHash) {
                            foundOwners.add(existing.owner || 'null');
                        }
                    }
                    for (const channelInfoHash of channelInfoHashes) {
                        // Get the idHash for this ChannelInfo
                        const channelInfoObj = await getObject(channelInfoHash);
                        const channelInfoIdHash = await calculateIdHashOfObj(channelInfoObj);
                        // Check discriminator filter if specified
                        if (options?.discriminator !== undefined && channelInfoObj.discriminator !== options.discriminator) {
                            continue;
                        }
                        // Get ALL version nodes for this channel ID hash
                        // Each version node may have a different owner
                        try {
                            const versionNodes = await getVersionsNodes(channelInfoIdHash);
                            // Collect ALL concurrent versions per owner (not just highest depth)
                            // Concurrent updates create forked versions - we need ALL of them to see all messages
                            const ownerVersions = new Map();
                            for (const versionNode of versionNodes) {
                                try {
                                    const channelInfo = await getObject(versionNode.data);
                                    const ownerKey = channelInfo.owner || 'null';
                                    // Skip if we already have this owner in selectedChannelInfos
                                    if (foundOwners.has(ownerKey)) {
                                        continue;
                                    }
                                    // Skip if owner filter specified and doesn't match
                                    if (owners && !owners.includes(channelInfo.owner === undefined ? null : channelInfo.owner)) {
                                        continue;
                                    }
                                    // Skip if discriminator doesn't match
                                    if (options?.discriminator !== undefined && channelInfo.discriminator !== options.discriminator) {
                                        continue;
                                    }
                                    // Collect ALL versions for this owner (concurrent branches)
                                    if (!ownerVersions.has(ownerKey)) {
                                        ownerVersions.set(ownerKey, []);
                                    }
                                    ownerVersions.get(ownerKey).push({ info: channelInfo, depth: versionNode.depth, hash: versionNode.data });
                                }
                                catch (versionError) {
                                    MessageBus.send('debug', `[CHANNEL] Failed to load version node data: ${String(versionError)}`);
                                }
                            }
                            // Add ALL concurrent versions for each owner to results
                            // This ensures messages from all forks are visible
                            for (const [ownerKey, versions] of ownerVersions) {
                                foundOwners.add(ownerKey);
                                // Deduplicate by head hash to avoid duplicate messages
                                const seenHeads = new Set();
                                for (const { info: channelInfo, depth } of versions) {
                                    const headKey = channelInfo.head || 'null';
                                    if (!seenHeads.has(headKey)) {
                                        seenHeads.add(headKey);
                                        console.log(`[ChannelManager] 📥 Discovered owner channel from version nodes: owner=${channelInfo.owner?.substring(0, 8) || 'null'}, participants=${participantsHash.substring(0, 8)}, head=${channelInfo.head?.substring(0, 8) || 'null'}, depth=${depth}`);
                                        selectedChannelInfos.push(channelInfo);
                                    }
                                }
                            }
                        }
                        catch (versionNodesError) {
                            // Fall back to getCurrentVersion if getVersionsNodes fails
                            MessageBus.send('debug', `[CHANNEL] getVersionsNodes failed, falling back: ${String(versionNodesError)}`);
                            const channelInfo = await getCurrentVersion(channelInfoIdHash);
                            const ownerKey = channelInfo.owner || 'null';
                            if (!foundOwners.has(ownerKey)) {
                                if (!owners || owners.includes(channelInfo.owner === undefined ? null : channelInfo.owner)) {
                                    foundOwners.add(ownerKey);
                                    selectedChannelInfos.push(channelInfo);
                                }
                            }
                        }
                    }
                }
                catch (discoveryError) {
                    // Reverse map query failed - not fatal, continue with cache results
                    MessageBus.send('debug', `[CHANNEL] Storage discovery failed for ${participantsHash.substring(0, 8)}: ${String(discoveryError)}`);
                }
            }
        }
        // Channel selection by Channel object
        // Calculate the id hashes of the matching channels
        // With owner as part of identity, each owner has their own channel idHash
        if (channels) {
            const idHashes = await Promise.all(channels.map(channel => calculateIdHashOfObj({
                $type$: 'ChannelInfo',
                participants: channel.participants,
                ...(channel.discriminator !== undefined && { discriminator: channel.discriminator }),
                ...(channel.owner !== undefined && { owner: channel.owner })
            })));
            selectedChannelIdHashes.push(...idHashes);
        }
        // Channel selection by explicit versions
        // Get the ChannelInfo objects from the instance and add them to the list.
        if (channelInfoHashes) {
            selectedChannelInfos.push(...(await Promise.all(channelInfoHashes.map(channelInfoHash => getObject(channelInfoHash)))));
        }
        // Channel selection by the id hash
        if (channelInfoIdHashes) {
            selectedChannelIdHashes.push(...channelInfoIdHashes);
        }
        // Channel selection by specific object id
        if (ids) {
            selectedChannelIdHashes.push(...ids.map(id => ChannelManager.decodeEntryId(id).channelInfoIdHash));
        }
        // If no selection was done, then return all of them
        if (!participantsList &&
            !owners &&
            !channels &&
            !channelInfoHashes &&
            !channelInfoIdHashes &&
            !ids) {
            for (const channelInfo of this.channelInfoCache.values()) {
                selectedChannelInfos.push(channelInfo);
            }
        }
        // #### Channel Id to latest merged version conversion ####
        // For the selection methods that just returned channel ids we need to get the latest
        // merged versions
        if (selectedChannelIdHashes) {
            for (const channelInfoIdHash of selectedChannelIdHashes) {
                const channelInfo = this.channelInfoCache.get(channelInfoIdHash);
                if (!channelInfo) {
                    throw new Error(`Channel ${channelInfoIdHash} does not exist!`);
                }
                selectedChannelInfos.push(channelInfo);
            }
        }
        // Remove duplicates. Since these are pointers to the objects in the cache
        // the unification works based on the addresses of the ChannelInfo instance in the cache.
        const uniqueSelection = Array.from(new Set(selectedChannelInfos));
        logWithParticipants(null, null, `getMatchingChannelInfos - End: selected ${uniqueSelection.length} channels/versions`);
        // Log cache state
        MessageBus.send('debug', `[CHANNEL_INSTANCE] Current cache state in getMatchingChannelInfos (${this.instanceId}): ${this.channelInfoCache.size} channels`);
        // loads head if possible
        return Promise.all(uniqueSelection.map(async (channelInfo) => {
            const channelInfoIdHash = await calculateIdHashOfObj(channelInfo);
            MessageBus.send('debug', `[CHANNEL] Verifying channel info for query - participants: ${channelInfo.participants.substring(0, 8)}, owner: ${channelInfo.owner || 'null'}, hash: ${channelInfoIdHash}`);
            try {
                // Always get the latest version from storage to ensure we have the correct head
                const latestVersion = await getCurrentVersion(channelInfoIdHash);
                if (channelInfo.head !== latestVersion.head) {
                    MessageBus.send('debug', `[CHANNEL] Head mismatch detected - participants: ${channelInfo.participants.substring(0, 8)}, cached head: ${channelInfo.head || 'null'}, stored head: ${latestVersion.head || 'null'}`);
                    // Update cache with correct head
                    this.channelInfoCache.set(channelInfoIdHash, latestVersion);
                    return latestVersion;
                }
                return {
                    ...channelInfo,
                    head: channelInfo.head || latestVersion.head
                };
            }
            catch (error) {
                MessageBus.send('debug', `[CHANNEL] Error refreshing channel info - participants: ${channelInfo.participants.substring(0, 8)}, error: ${String(error)}`);
                // Fallback to original info with head check
                return {
                    ...channelInfo,
                    head: channelInfo.head
                };
            }
        }));
    }
    // ######## Hook implementation for merging and adding channels ########
    /**
     * Handler function for the VersionedObj
     * @param caughtObject
     */
    async processNewVersion(caughtObject) {
        try {
            MessageBus.send('log', `processNewVersion ${caughtObject.idHash} - new version ${caughtObject.hash}`);
            await this.addChannelIfNotExist(caughtObject.idHash);
            const emitEvents = await serializeWithType(`ChannelManager.processNewVersion ${caughtObject.idHash}`, async () => {
                const newChannelInfo = caughtObject.obj;
                const oldChannelInfo = this.channelInfoCache.get(caughtObject.idHash);
                this.channelInfoCache.set(caughtObject.idHash, newChannelInfo);
                MessageBus.send('log', `processNewVersion ${caughtObject.idHash} - new head: ${newChannelInfo.head}, old head: ${oldChannelInfo?.head}`);
                const changedElements = [];
                if (oldChannelInfo === undefined || oldChannelInfo.head === undefined) {
                    for await (const elem of ChannelManager.entryIterator(newChannelInfo)) {
                        changedElements.push({ ...elem, isNew: true });
                    }
                }
                else {
                    for await (const elem of ChannelManager.mergeIteratorMostCurrent([
                        ChannelManager.entryIterator(oldChannelInfo),
                        ChannelManager.entryIterator(newChannelInfo)
                    ], false, false)) {
                        changedElements.push({ ...elem, isNew: elem.iterIndex !== 0 });
                    }
                }
                // Register profile at leute by creating a version history.
                for (const entry of changedElements) {
                    if (!this.getChannelSettingsRegisterSenderProfileAtLeute(entry.channelInfoIdHash)) {
                        continue;
                    }
                    if (!entry.isNew) {
                        continue;
                    }
                    const myId = await this.leuteModel.myMainIdentity();
                    if (entry.metaDataHashes) {
                        for (const metaDataHash of entry.metaDataHashes) {
                            const metadataObj = await getObject(metaDataHash);
                            if (metadataObj.$type$ === 'Profile') {
                                const newProfile = {
                                    ...metadataObj,
                                    owner: myId
                                };
                                try {
                                    const current = await getObjectByIdObj(newProfile);
                                    newProfile.$versionHash$ = current.obj.$versionHash$;
                                }
                                catch (_e) {
                                    // Empty, because this means that no version exists, yet
                                }
                                await storeVersionedObject(newProfile);
                            }
                        }
                    }
                }
                MessageBus.send('debug', `processNewVersion ${caughtObject.idHash} - found ${changedElements.length} new elements`);
                if (changedElements.length > 0) {
                    return () => {
                        this.onUpdated.emit(caughtObject.idHash, newChannelInfo.participants, newChannelInfo.owner || null, new Date(changedElements[changedElements.length - 1].creationTime), changedElements);
                    };
                }
                else {
                    // IMPORTANT: Even with no new elements, emit for NEW channels so createChannel's
                    // waiting Promise can resolve. This happens when creating an empty channel.
                    const isNewChannel = oldChannelInfo === undefined || oldChannelInfo.head === undefined;
                    return () => {
                        if (isNewChannel) {
                            this.onUpdated.emit(caughtObject.idHash, newChannelInfo.participants, newChannelInfo.owner || null, new Date(), []);
                        }
                        // Otherwise no emit - just an update with no new content
                    };
                }
            });
            emitEvents();
        }
        catch (e) {
            console.error(e); // Introduce an error event later!
        }
    }
    /**
     * Add the passed channel to the cache & registry if it is not there, yet.
     *
     * @param channelInfoIdHash - the channel to add to the registry
     */
    async addChannelIfNotExist(channelInfoIdHash) {
        // Determine the channel participants and owner
        // Note: Use getObjectByIdHash to get the full object since owner is no longer part of identity
        let channelParticipants;
        let channelOwner;
        {
            const channelInfoResult = await getObjectByIdHash(channelInfoIdHash);
            channelParticipants = channelInfoResult.obj.participants;
            channelOwner = channelInfoResult.obj.owner;
        }
        // Add debug logs
        MessageBus.send('debug', `[CHANNEL] addChannelIfNotExist - Checking: ${channelParticipants.substring(0, 8)}, owner: ${channelOwner || 'null'}`);
        try {
            // Check if it exists in cache
            const inCache = this.channelInfoCache.has(channelInfoIdHash);
            MessageBus.send('debug', `[CHANNEL] Channel exists in cache: ${inCache}`);
            // Check if it exists in storage
            try {
                const existsInStorage = await getObjectByIdHash(channelInfoIdHash);
                MessageBus.send('debug', `[CHANNEL] Channel exists in storage: ${existsInStorage}`);
            }
            catch (error) {
                MessageBus.send('debug', `[CHANNEL] Storage check error: ${String(error)}`);
            }
        }
        catch (error) {
            MessageBus.send('debug', `[CHANNEL] Error in debug logging: ${String(error)}`);
        }
        try {
            await serializeWithType(`${ChannelManager.cacheLockName}${channelInfoIdHash}`, async () => {
                logWithParticipants(channelParticipants, channelOwner, 'addChannelIfNotExist - START');
                if (this.channelInfoCache.has(channelInfoIdHash)) {
                    logWithParticipants(channelParticipants, channelOwner, 'addChannelIfNotExist - END: already existed');
                }
                else {
                    const result = await getObjectByIdHash(channelInfoIdHash);
                    this.channelInfoCache.set(channelInfoIdHash, result.obj);
                    await this.saveRegistryCacheToOne();
                    logWithParticipants(channelParticipants, channelOwner, 'addChannelIfNotExist - END: added');
                }
            });
        }
        catch (e) {
            logWithParticipants(channelParticipants, channelOwner, `addChannelIfNotExist - FAIL: ${String(e)}`);
            throw e;
        }
    }
    // ######## One Channel registry read / write methods ########
    /**
     * Save the cache content as new version of registry in one.
     */
    async saveRegistryCacheToOne() {
        await serializeWithType(ChannelManager.registryLockName, async () => {
            // Write the registry version
            this.loadedRegistryVersion = (await storeVersionedObject({
                $type$: 'ChannelRegistry',
                id: 'ChannelRegistry',
                $versionHash$: this.loadedRegistryVersion,
                channels: new Set(this.channelInfoCache.keys())
            })).obj.$versionHash$;
        });
    }
    /**
     * Load the latest channel registry version into the the cache.
     */
    async loadRegistryCacheFromOne() {
        logWithParticipants(null, null, 'loadRegistryCacheFromOne - START');
        MessageBus.send('debug', `[CHANNEL_REGISTRY] START loading registry into cache`);
        await serializeWithType(ChannelManager.registryLockName, async () => {
            // If the cache is not empty, then something is wrong.
            // The current implementation only needs to populate it once - at init and there it
            // should be empty.
            if (this.channelInfoCache.size > 0) {
                MessageBus.send('debug', `[CHANNEL_REGISTRY] ERROR: Cache is not empty (size: ${this.channelInfoCache.size})`);
                throw new Error('Populating the registry cache is only allowed if it is empty!');
            }
            // Get the registry. If it does not exist, start with an empty cache (so just return)
            let registry;
            try {
                MessageBus.send('debug', `[CHANNEL_REGISTRY] Fetching registry by ID: ChannelRegistry`);
                registry = (await getObjectByIdObj({ $type$: 'ChannelRegistry', id: 'ChannelRegistry' })).obj;
                MessageBus.send('debug', `[CHANNEL_REGISTRY] Found registry with ${registry.channels.size} channels`);
                // Log all channels found in registry
                if (registry.channels.size < 100) {
                    const channelList = [...registry.channels];
                    MessageBus.send('debug', `[CHANNEL_REGISTRY] Registry channel IDs: ${JSON.stringify(channelList)}`);
                }
                else {
                    MessageBus.send('debug', `[CHANNEL_REGISTRY] Registry contains too many channels (${registry.channels.size}) to log IDs`);
                }
            }
            catch (e) {
                MessageBus.send('debug', `[CHANNEL_REGISTRY] No channel registry found, error: ${String(e)}`);
                return;
            }
            // We load the latest merged version for all channels
            // Warning: this might be very memory hungry, because we load this stuff in parallel,
            // so potentially all version maps of all channels are in memory simultaneously.
            // This issue should be fixed by allowing partial version loads or by changing the
            // way that version maps work - or by not using version maps at all.
            // Short term fix might be by serializing all the loads, but this will significantly
            // increase load time - so let's stick with the parallel version for now.
            MessageBus.send('debug', `[CHANNEL_REGISTRY] Loading ${registry.channels.size} channels from registry`);
            const startTime = Date.now();
            let successCount = 0;
            let errorCount = 0;
            await Promise.all([...registry.channels].map(async (channelIdHash) => {
                try {
                    MessageBus.send('debug', `[CHANNEL_REGISTRY] Loading channel with ID hash: ${channelIdHash}`);
                    const channelInfoResult = await getObjectByIdHash(channelIdHash);
                    // Get channel details
                    const channelParticipants = channelInfoResult.obj.participants;
                    const channelOwner = channelInfoResult.obj.owner;
                    const channelHead = channelInfoResult.obj.head;
                    this.channelInfoCache.set(channelIdHash, channelInfoResult.obj);
                    MessageBus.send('debug', `[CHANNEL_REGISTRY] Loaded channel: ${channelParticipants.substring(0, 8)}, owner: ${channelOwner || 'null'}, head: ${channelHead || 'null'}, version: ${channelInfoResult.hash}`);
                    if (channelHead) {
                        try {
                            const headEntry = await getObject(channelHead);
                            const entriesCount = await this.countChannelEntries(channelHead);
                            MessageBus.send('debug', `[CHANNEL_REGISTRY] Channel ${channelParticipants.substring(0, 8)} has ${entriesCount} entries, head: ${channelHead}, previous: ${headEntry.previous || 'null'}`);
                        }
                        catch (headError) {
                            MessageBus.send('debug', `[CHANNEL_REGISTRY] Error getting head entry for channel ${channelParticipants.substring(0, 8)}: ${String(headError)}`);
                        }
                    }
                    else {
                        MessageBus.send('debug', `[CHANNEL_REGISTRY] Channel ${channelParticipants.substring(0, 8)} has no head (empty channel)`);
                    }
                    successCount++;
                }
                catch (e) {
                    // This means that there is no version - should not happen. The empty
                    // channel is also a version
                    MessageBus.send('debug', `[CHANNEL_REGISTRY] Error loading channel ${channelIdHash}: ${String(e)}`);
                    errorCount++;
                }
            }));
            const endTime = Date.now();
            MessageBus.send('debug', `[CHANNEL_REGISTRY] Loaded ${successCount} channels successfully, ${errorCount} failures, time: ${endTime - startTime}ms`);
            // List channel counts by participant count
            const channelsByParticipantCount = new Map();
            for (const channelInfo of this.channelInfoCache.values()) {
                // Categorize channels by number of participants
                // We can't easily count participants without loading the HashGroup,
                // so for now just track total channels
                const count = channelsByParticipantCount.get(0) || 0;
                channelsByParticipantCount.set(0, count + 1);
            }
            MessageBus.send('debug', `[CHANNEL_REGISTRY] Total channels: ${this.channelInfoCache.size}`);
            this.loadedRegistryVersion = registry.$versionHash$;
            MessageBus.send('debug', `[CHANNEL_REGISTRY] Registry version loaded: ${registry.$versionHash$}`);
        });
        MessageBus.send('debug', `[CHANNEL_REGISTRY] DONE loading registry into cache, total channels: ${this.channelInfoCache.size}`);
        logWithParticipants(null, null, 'loadRegistryCacheFromOne - END');
    }
    /**
     * Count entries in a channel by traversing the linked list
     * @private
     */
    async countChannelEntries(headHash) {
        let count = 0;
        let currentHash = headHash;
        while (currentHash) {
            count++;
            try {
                const currentEntry = await getObject(currentHash);
                currentHash = currentEntry.previous;
                // Safety limit to avoid infinite loops in case of circular references
                if (count > 10000) {
                    MessageBus.send('debug', `[CHANNEL_REGISTRY] Stopping entry count at 10000+ entries`);
                    break;
                }
            }
            catch (e) {
                MessageBus.send('debug', `[CHANNEL_REGISTRY] Error traversing entry ${currentHash}: ${String(e)}`);
                break;
            }
        }
        return count;
    }
    // ######## Access stuff ########
    /**
     * Get the person list with whom this channel is shared.
     *
     * This list also explodes the access groups and adds those persons to the returned list.
     *
     * @param channelInfoIdHash
     * @returns
     */
    static async sharedWithPersonsList(channelInfoIdHash) {
        /**
         * Get the persons from the groups and persons of the passed access object.
         *
         * @param accessHash
         * @returns
         */
        async function extractPersonsFromIdAccessObject(accessIdHash) {
            // Use getObjectByIdHash for ID hashes (not getObjectWithType which expects concrete hashes)
            const accessResult = await getObjectByIdHash(accessIdHash);
            const accessObject = accessResult.obj;
            let allSharedPersons = [];
            if (accessObject.hashGroup.length > 0) {
                const groupPersons = await Promise.all(
                // accessObject.hashGroup contains HashGroup hashes directly
                accessObject.hashGroup.map(async (hashGroupHash) => {
                    const hashGroupObject = await getObject(hashGroupHash);
                    return Array.from(hashGroupObject.person);
                }));
                allSharedPersons = allSharedPersons.concat(groupPersons.reduce((acc, val) => acc.concat(val), []));
            }
            if (accessObject.person.length > 0) {
                allSharedPersons = allSharedPersons.concat(accessObject.person);
            }
            return allSharedPersons;
        }
        // Extract the access objects pointing to the channel info
        // IMPORTANT: IdAccess is an ID object, use getAllIdObjectEntries (not getAllEntries)
        const channelAccessObjects = await getAllIdObjectEntries(channelInfoIdHash, 'IdAccess');
        const personNested = await Promise.all(channelAccessObjects.map(async (value) => extractPersonsFromIdAccessObject(value)));
        const personsFlat = personNested.reduce((acc, val) => acc.concat(val), []);
        // Remove duplicate persons and return the result
        return [...new Set(personsFlat)];
    }
    /**
     * This places the new elements on top of the old head thus extending the linked list.
     *
     * @param oldHead
     * @param newElementsReversed
     */
    static async rebuildEntries(oldHead, newElementsReversed) {
        // Create the new channel entries linked list from the array elements
        let lastChannelEntry = oldHead;
        let newEntryResult;
        for (let i = newElementsReversed.length - 1; i >= 0; --i) {
            newEntryResult = await storeUnversionedObject({
                $type$: 'LinkedListEntry',
                data: newElementsReversed[i].creationTimeHash,
                metadata: newElementsReversed[i].metaDataHashes,
                previous: lastChannelEntry
            });
            lastChannelEntry = newEntryResult.hash;
        }
        // If newEntryResult is undefined this means, that the newElementsReserved list was empty
        // Usually we could just return the oldHead, but we need an UnversionedObjectResult from
        // a SHA256Hash<ChannelEntry> and I have no clue how to get it, so throw.
        if (!newEntryResult) {
            throw new Error('It does not make sense to rebuild a channel with 0 elements.');
        }
        // Create the new channel version
        return newEntryResult;
    }
    /**
     * Post a new entry in a channel.
     *
     * This creates a new channel entry with the current time as creation time and
     * inserts it to the channel.
     *
     * Attention: This is an impure plan, because it always generates a new element
     *            with a new creation time even if the payload was posted before
     */
    /**
     * Get participants array from a HashGroup hash.
     * @param participantsHash - Hash of the HashGroup containing participants
     * @returns Array of participant Person IDs
     */
    async getParticipantsFromHash(participantsHash) {
        const hashGroup = await getObject(participantsHash);
        return Array.from(hashGroup.person);
    }
    /**
     * Internal implementation of posting to a channel.
     * @param participants - The participants hash of the channel
     * @param channelOwner - Owner of the channel to post to
     * @param payload - Payload of the post
     * @param author - Author of chat message
     * @param timestamp - Timestamp that is used as creation time
     * @param discriminator - Optional discriminator for multi-topic support
     * @returns
     */
    async internalChannelPost(participants, channelOwner, payload, author, timestamp, discriminator) {
        const postId = Date.now().toString(36) + Math.random().toString(36).substring(2, 6);
        const t0 = Date.now();
        console.log(`[CHANNEL-POST:${postId}] START internalChannelPost participants=${participants.substring(0, 8)} owner=${channelOwner?.substring(0, 8) || 'none'}`);
        // Owner is required for proper multi-peer channel operation
        if (!channelOwner) {
            throw new Error(`Cannot post to channel without owner. Participants: ${participants.substring(0, 16)}`);
        }
        // Load latest ChannelInfo (identity is based on participants + discriminator + owner)
        const channelInfoIdHash = await calculateIdHashOfObj({
            $type$: 'ChannelInfo',
            participants: participants,
            ...(discriminator !== undefined && { discriminator }),
            owner: channelOwner
        });
        console.log(`[CHANNEL-POST:${postId}] T+${Date.now() - t0}ms: calculated idHash=${channelInfoIdHash.substring(0, 8)}`);
        const latestChannelInfo = (await getObjectByIdHash(channelInfoIdHash)).obj;
        console.log(`[CHANNEL-POST:${postId}] T+${Date.now() - t0}ms: loaded latest ChannelInfo, head=${latestChannelInfo.head?.substring(0, 8) || 'null'}, versionHash=${latestChannelInfo.$versionHash$?.substring(0, 8) || 'none'}`);
        // Insert new element into linked list
        const newHeadHash = await linkedListInsert(latestChannelInfo.head, payload, async (creationTimeResult) => {
            // Collect metadata that shall be included
            const metadata = [];
            if (author !== undefined) {
                console.log(`[CHANNEL-POST:${postId}] metadata: BEFORE affirm, author=${author.substring(0, 8)}`);
                const affirmResult = await this.leuteModel.trust.affirm(creationTimeResult.hash, author);
                console.log(`[CHANNEL-POST:${postId}] metadata: AFTER affirm, signatureHash=${affirmResult.hash.substring(0, 8)}`);
                metadata.push(affirmResult.hash);
                if (this.getChannelSettingsAppendSenderProfile(channelInfoIdHash)) {
                    console.log(`[CHANNEL-POST:${postId}] metadata: looking for profile, author=${author.substring(0, 8)}`);
                    const profiles = await (await this.leuteModel.me()).profiles(author);
                    console.log(`[CHANNEL-POST:${postId}] metadata: found ${profiles.length} profiles for author`);
                    const defaultProfile = profiles.filter(profile => profile.owner === author &&
                        profile.personId === author);
                    console.log(`[CHANNEL-POST:${postId}] metadata: ${defaultProfile.length} matching self-owned profiles (owner===author && personId===author)`);
                    if (defaultProfile.length > 0 &&
                        defaultProfile[0].loadedVersion !== undefined) {
                        metadata.push(defaultProfile[0].loadedVersion);
                        console.log(`[CHANNEL-POST:${postId}] metadata: added profile version ${defaultProfile[0].loadedVersion.substring(0, 8)}`);
                    }
                }
            }
            console.log(`[CHANNEL-POST:${postId}] metadata: returning ${metadata.length} metadata hashes`);
            return metadata;
        }, timestamp);
        console.log(`[CHANNEL-POST:${postId}] T+${Date.now() - t0}ms: linkedListInsert done, newHeadHash=${newHeadHash.substring(0, 8)}`);
        // processNewVersion is triggered automatically by the listener for new objects
        // registered in init()
        // Write the channel info with the new channel entry as head
        console.log(`[CHANNEL-POST:${postId}] T+${Date.now() - t0}ms: BEFORE storeVersionedObject for ChannelInfo`);
        const newChannelInfo = await storeVersionedObject({
            $type$: 'ChannelInfo',
            $versionHash$: latestChannelInfo.$versionHash$,
            participants: participants,
            ...(discriminator !== undefined && { discriminator }),
            owner: channelOwner,
            head: newHeadHash
        });
        console.log(`[CHANNEL-POST:${postId}] T+${Date.now() - t0}ms: AFTER storeVersionedObject - hash=${newChannelInfo.hash.substring(0, 8)} idHash=${newChannelInfo.idHash.substring(0, 8)} status=${newChannelInfo.status}`);
        // Verify the cache has the correct head pointer
        const cachedInfo = this.channelInfoCache.get(channelInfoIdHash);
        if (!cachedInfo || cachedInfo.head !== newChannelInfo.obj.head) {
            MessageBus.send('debug', `[CHANNEL] Cache verification failed - participants: ${participants.substring(0, 8)}, expected head: ${newChannelInfo.obj.head}, cached head: ${cachedInfo?.head || 'null'}`);
            // Force update the cache
            this.channelInfoCache.set(channelInfoIdHash, newChannelInfo.obj);
            MessageBus.send('debug', `[CHANNEL] Cache forcibly updated with correct head pointer - participants: ${participants.substring(0, 8)}`);
        }
        console.log(`[CHANNEL-POST:${postId}] T+${Date.now() - t0}ms: END internalChannelPost`);
        return newChannelInfo;
    }
    /**
     * Notify sibling channels that new data is available in a related channel.
     *
     * USE CASE: When AI posts to AI's channel (local post), listeners watching the owner's
     * channel won't know there's new data because the owner's channel version wasn't updated.
     * This method explicitly emits onUpdated so UI components can refresh and merge all
     * sibling channels.
     *
     * This replicates what happens during CHUM sync when receiving channel updates from peers.
     * Should only be called for local AI and owner channel posts where sibling notification
     * is needed.
     *
     * @param participants - The participants hash identifying the channel group
     * @param discriminator - Optional discriminator for the channel group
     * @param channelOwner - The owner of the channel that was updated
     */
    async notifySiblingChannels(participants, discriminator, channelOwner) {
        try {
            // Get the updated channel info from cache
            const channelInfoIdHash = await calculateIdHashOfObj({
                $type$: 'ChannelInfo',
                participants,
                ...(discriminator !== undefined && { discriminator }),
                owner: channelOwner
            });
            const cachedChannelInfo = this.channelInfoCache.get(channelInfoIdHash);
            if (!cachedChannelInfo || !cachedChannelInfo.head) {
                MessageBus.send('debug', `[CHANNEL] notifySiblingChannels: no cached channel or empty head`);
                return;
            }
            // Find all sibling channels with same participants+discriminator
            const siblingChannels = await this.getMatchingChannelInfos({
                participants,
                discriminator
            });
            // If there's only one channel (the updated one), no siblings to notify
            if (siblingChannels.length <= 1) {
                return;
            }
            // Get the latest entry data for the notification
            const changedElements = [];
            for await (const elem of ChannelManager.entryIterator(cachedChannelInfo)) {
                changedElements.push({ ...elem, isNew: true });
                // Only get the latest entry (the one just posted)
                break;
            }
            if (changedElements.length === 0) {
                return;
            }
            const creationTime = new Date(changedElements[0].creationTime);
            MessageBus.send('debug', `[CHANNEL] Notifying sibling channels - participants: ${participants.substring(0, 8)}, ` +
                `${siblingChannels.length} total channels, updated owner: ${channelOwner?.substring(0, 8) || 'null'}`);
            // Emit onUpdated - listeners filtering by participants will receive this
            // and can refresh their message views by calling retrieveAllMessages()
            // which merges all sibling channels including this one
            this.onUpdated.emit(channelInfoIdHash, participants, channelOwner || null, creationTime, changedElements);
        }
        catch (error) {
            MessageBus.send('debug', `[CHANNEL] Error notifying sibling channels: ${String(error)}`);
            // Non-fatal - the main post succeeded, sibling notification is best-effort
        }
    }
    /**
     * Returns the default owner for channel operations.
     * Uses manually set defaultOwner if available, otherwise uses main identity.
     * Throws if no identity is available - owner is required for proper channel operation.
     */
    async calculateDefaultOwner() {
        if (this.defaultOwner !== undefined) {
            return this.defaultOwner;
        }
        const mainId = await this.leuteModel.myMainIdentity();
        if (!mainId) {
            throw new Error('Cannot determine channel owner: no main identity available. Ensure LeuteModel is initialized before channel operations.');
        }
        return mainId;
    }
}
//# sourceMappingURL=ChannelManager.js.map